// ============================================================================
// TAX SERVICE - PKCash Tax Tracking & 1099 Reporting
// ============================================================================
// Handles tax calculations for PKCash (real money equivalent).
// PKCash represents dollar equivalent - all amounts are in USD.
// 1099-MISC threshold: $600/year for miscellaneous income
// ============================================================================

import { auth } from '../firebase';
import { API_BASE } from '../utils/apiBase';

const THRESHOLD_1099 = 600; // $600 USD threshold for 1099-MISC

// ============================================================================
// API UTILITIES
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall<T>(endpoint: string, options: {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: object;
  params?: Record<string, string>;
}): Promise<T> {
  const token = await getAuthToken();
  
  let url = `${API_BASE}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams(options.params);
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API call failed: ${response.status}`);
  }
  
  return data;
}

// ============================================================================
// TYPES
// ============================================================================

export interface TaxSummary {
  userId: string;
  year: number;
  grossWinnings: number;
  grossCommissions: number;
  grossBonuses: number;
  totalGrossIncome: number;
  netLosses: number;
  netIncome: number;
  totalWithdrawals: number;
  requires1099: boolean;
  threshold1099: number;
  amountOver1099: number;
  estimatedTax: number;
  taxBracket: string;
  monthlyIncome: Record<string, {
    winnings: number;
    commissions: number;
    bonuses: number;
    withdrawals: number;
  }>;
}

export interface AgentTaxSummary {
  totalCommissions: number;
  monthlyCommissions: Record<string, number>;
  requires1099: boolean;
  estimatedTax: number;
  taxBracket: string;
  threshold1099: number;
}

export interface Platform1099Report {
  year: number;
  totalUsers: number;
  usersRequiring1099: number;
  totalReportableIncome: number;
  threshold1099: number;
  userBreakdown: Array<{
    odD: string;
    displayName: string;
    email: string;
    totalIncome: number;
    w9Status: string;
    form1099Status: string;
  }>;
  generatedAt: number;
}

// ============================================================================
// TAX SERVICE FUNCTIONS
// ============================================================================

/**
 * Get tax summary for current user for a specific year
 */
export async function getUserTaxSummary(year?: number): Promise<TaxSummary | null> {
  try {
    const targetYear = year || new Date().getFullYear();
    const response = await apiCall<{ success: boolean; taxSummary: TaxSummary }>(
      '/getTaxSummary',
      { method: 'GET', params: { year: targetYear.toString() } }
    );
    return response.taxSummary;
  } catch (error) {
    console.error('[TaxService] Error getting tax summary:', error);
    return null;
  }
}

/**
 * Get agent-specific tax summary (commissions focused)
 */
export async function getAgentTaxSummary(year?: number): Promise<AgentTaxSummary> {
  try {
    const targetYear = year || new Date().getFullYear();
    const response = await apiCall<AgentTaxSummary & { success: boolean }>(
      '/getAgentTaxSummary',
      { method: 'GET', params: { year: targetYear.toString() } }
    );
    return {
      totalCommissions: response.totalCommissions,
      monthlyCommissions: response.monthlyCommissions,
      requires1099: response.requires1099,
      estimatedTax: response.estimatedTax,
      taxBracket: response.taxBracket,
      threshold1099: response.threshold1099,
    };
  } catch (error) {
    console.error('[TaxService] Error getting agent tax summary:', error);
    return {
      totalCommissions: 0,
      monthlyCommissions: {},
      requires1099: false,
      estimatedTax: 0,
      taxBracket: 'N/A',
      threshold1099: THRESHOLD_1099,
    };
  }
}

/**
 * Get platform-wide 1099 report (admin only)
 */
export async function getPlatform1099Report(year?: number): Promise<Platform1099Report> {
  try {
    const targetYear = year || new Date().getFullYear();
    const response = await apiCall<{ success: boolean; report: Platform1099Report }>(
      '/getPlatformTaxReport',
      { method: 'GET', params: { year: targetYear.toString() } }
    );
    return response.report;
  } catch (error) {
    console.error('[TaxService] Error getting platform tax report:', error);
    throw error;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const taxService = {
  getUserTaxSummary,
  getAgentTaxSummary,
  getPlatform1099Report,
  THRESHOLD_1099,
};

export default taxService;